import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Home as HomeIcon, RefreshCw, Search } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';
import { avatarUrl } from '@/lib/avatar-url';
import { cn, formatDuration, formatTime } from '@/lib/utils';

type Role = 'EMPLOYEE' | 'MANAGER' | 'HR_ADMIN' | 'SUPER_ADMIN';
type PunchType = 'IN' | 'OUT';
type DeclaredLocation = 'OFFICE' | 'REMOTE';

interface TeamLastPunch {
  type: PunchType;
  eventAt: string;
  declaredLocation: DeclaredLocation;
}

interface TeamMember {
  id: string;
  fullName: string;
  email: string;
  role: Role;
  lastPunchToday: TeamLastPunch | null;
  balanceMinutesMonth: number;
  vacationBalanceDays: number;
  profileColor: string | null;
  avatarUpdatedAt: string | null;
}

export function Team() {
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();

  const teamQuery = useQuery({
    queryKey: ['team'],
    queryFn: () => api<TeamMember[]>('/team'),
  });

  const members = useMemo(() => teamQuery.data ?? [], [teamQuery.data]);
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        m.fullName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    );
  }, [members, filter]);

  const summary = useMemo(() => {
    const inside = members.filter((m) => m.lastPunchToday?.type === 'IN').length;
    const outside = members.filter((m) => m.lastPunchToday?.type === 'OUT').length;
    const noPunch = members.length - inside - outside;
    return { inside, outside, noPunch };
  }, [members]);

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish">
              Equipo
              <span className="text-muted-foreground font-normal"> · {members.length} personas</span>
            </h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Estado de tus empleados: fichaje del día, balance del mes y vacaciones disponibles.
            </p>
          </div>
          <div className="inline-flex items-center gap-3 flex-wrap">
            <div className="relative w-72 max-w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Buscar nombre o email…"
                className="pl-9"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => teamQuery.refetch()}
              disabled={teamQuery.isFetching}
            >
              <RefreshCw
                className={cn('h-3.5 w-3.5', teamQuery.isFetching && 'animate-spin')}
              />
              Actualizar
            </Button>
          </div>
        </header>

        {teamQuery.isLoading ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : teamQuery.isError ? (
          <p role="alert" className="px-7 py-10 text-center text-sm text-destructive">
            {teamQuery.error instanceof ApiError
              ? teamQuery.error.message
              : 'Error al cargar el equipo'}
          </p>
        ) : members.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            No tienes empleados asignados.
          </p>
        ) : (
          <>
            <div className="px-7 pt-6 pb-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
              <SummaryCard label="Dentro" value={summary.inside} tone="success" />
              <SummaryCard label="Fuera" value={summary.outside} />
              <SummaryCard label="Sin fichar" value={summary.noPunch} tone="warning" />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <Th>Empleado</Th>
                    <Th>Estado ahora</Th>
                    <Th>Último fichaje</Th>
                    <Th align="right">Balance mes</Th>
                    <Th align="right">Vacaciones</Th>
                    <Th align="right">{''}</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-5 py-8 text-center text-sm text-muted-foreground"
                      >
                        Sin resultados.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((m) => (
                      <MemberRow
                        key={m.id}
                        member={m}
                        onSelect={() => navigate(`/equipo/${m.id}`)}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase text-muted-foreground bg-paper border-b border-border px-5 py-3.5',
        align === 'right' ? 'text-right' : 'text-left',
      )}
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </th>
  );
}

function MemberRow({ member, onSelect }: { member: TeamMember; onSelect: () => void }) {
  const status = computeStatus(member);
  return (
    <tr
      className="border-b border-border cursor-pointer hover:bg-paper transition-colors"
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Ver jornada de ${member.fullName}`}
    >
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <Avatar
            fullName={member.fullName}
            color={member.profileColor}
            avatarUrl={avatarUrl(member.id, member.avatarUpdatedAt)}
            active={status.kind === 'in'}
          />
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-foreground leading-tight truncate">
              {member.fullName}
            </div>
            <div className="text-[12px] text-muted-foreground leading-tight truncate">
              {roleLabel(member.role)}
            </div>
          </div>
        </div>
      </td>
      <td className="px-5 py-4">
        <Badge tone={status.tone} dot={status.kind === 'in'}>
          {status.label}
        </Badge>
      </td>
      <td className="px-5 py-4">
        {member.lastPunchToday ? (
          <div className="flex items-center gap-2 text-[13px]">
            <Badge tone={member.lastPunchToday.type === 'IN' ? 'green' : 'neutral'}>
              {member.lastPunchToday.type}
            </Badge>
            <span className="font-mono tabular-nums">
              {formatTime(member.lastPunchToday.eventAt)}
            </span>
            {member.lastPunchToday.declaredLocation === 'OFFICE' ? (
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <HomeIcon className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td
        className={cn(
          'px-5 py-4 text-right font-mono tabular-nums',
          member.balanceMinutesMonth > 0 && 'text-success',
          member.balanceMinutesMonth < 0 && 'text-destructive',
        )}
      >
        {member.balanceMinutesMonth === 0
          ? '0h 00m'
          : (member.balanceMinutesMonth > 0 ? '+' : '') +
            formatDuration(member.balanceMinutesMonth)}
      </td>
      <td
        className={cn(
          'px-5 py-4 text-right font-mono tabular-nums',
          member.vacationBalanceDays < 0 && 'text-destructive font-semibold',
        )}
      >
        {formatDays(member.vacationBalanceDays)}
      </td>
      <td className="px-5 py-4 text-right">
        <span className="text-[13px] font-medium text-icaria-600 hover:text-icaria-700">
          Ver →
        </span>
      </td>
    </tr>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'warning';
}) {
  const toneCls =
    tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : 'text-foreground';
  return (
    <div className="border border-border rounded-sm bg-paper px-5 py-4">
      <p
        className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
        style={{ letterSpacing: '0.12em' }}
      >
        {label}
      </p>
      <p className={cn('font-mono text-[26px] font-medium tracking-tight2 mt-1 tabular-nums', toneCls)}>
        {value}
      </p>
    </div>
  );
}

type StatusKind = 'in' | 'out' | 'none';

function computeStatus(m: TeamMember): {
  label: string;
  tone: 'green' | 'neutral' | 'amber';
  kind: StatusKind;
} {
  if (!m.lastPunchToday) {
    return { label: 'Sin fichar', tone: 'amber', kind: 'none' };
  }
  if (m.lastPunchToday.type === 'IN') {
    return { label: 'Dentro', tone: 'green', kind: 'in' };
  }
  return { label: 'Fuera', tone: 'neutral', kind: 'out' };
}

function roleLabel(role: Role): string {
  switch (role) {
    case 'EMPLOYEE':
      return 'Empleado';
    case 'MANAGER':
      return 'Manager';
    case 'HR_ADMIN':
      return 'RRHH';
    case 'SUPER_ADMIN':
      return 'Admin';
  }
}

function formatDays(d: number): string {
  if (d === 0) return '0 días';
  const isInt = Math.abs(d - Math.round(d)) < 0.001;
  const formatted = isInt ? Math.round(d).toString() : d.toFixed(1);
  return `${formatted} ${Math.abs(d) === 1 ? 'día' : 'días'}`;
}
